import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const response = await apiService.getCurrentUser();
          if (response.success && response.user) {
            setUser(response.user);
          } else {
            localStorage.removeItem('authToken');
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          localStorage.removeItem('authToken');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiService.login({ email, password });
      if (response.success && response.user) {
        setUser(response.user);
        toast.success('Login successful');
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      toast.error(error.message || 'Login failed');
      throw error;
    }
  };

  const register = async (name, email, password, role, phone) => {
    try {
      const response = await apiService.register({ name, email, password, role, phone });
      if (response.success && response.user) {
        setUser(response.user);
        toast.success('Registration successful');
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message || 'Registration failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      toast.error('Logout failed');
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await apiService.updateProfile(data);
      if (response.success && response.user) {
        setUser(response.user);
        toast.success('Profile updated successfully');
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      toast.error(error.message || 'Profile update failed');
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await apiService.changePassword({ currentPassword, newPassword });
      if (response.success) {
        toast.success('Password changed successfully');
      } else {
        throw new Error(response.message || 'Password change failed');
      }
    } catch (error) {
      toast.error(error.message || 'Password change failed');
      throw error;
    }
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isMechanic = user?.role === 'mechanic';

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated,
    isAdmin,
    isMechanic,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};